'use client';

import React, { useState, ReactElement, useRef, useEffect } from 'react';
import { useDebounce } from 'use-debounce';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import FilterPill from '../FilterPill';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import ActivityTypeLabel from '../ActivityTypeLabel';
import { Checkbox, CircularProgress, Search } from 'src/components/PulseUI';

interface MultiSelectMenuProps {
  id: string;
  header: string;
  optionType: string;
  enableSearch?: boolean;
  options:
    | { label: string; id: number }[]
    | { label: string; id: number; icon: string; color: string }[];
  startDecorator: ReactElement;
  isActivity?: boolean;
  selectedOptions: string[];
  setSelectedOptions: any;
  onMenuScroll: (isActivity: boolean) => void;
  isLoading: boolean;
}

const MultiSelectMenu = ({
  id,
  header,
  optionType,
  enableSearch,
  options,
  startDecorator,
  isActivity = false,
  selectedOptions,
  setSelectedOptions,
  onMenuScroll,
  isLoading,
}: MultiSelectMenuProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const moreOptionsMenuRef = useRef<HTMLDivElement>(null);
  const { userQueryParams, setUserQueryParams } = useDashboard();

  const [isOpen, setIsOpen] = useState(false);
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [allSelected, setAllSelected] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  useEffect(() => {
    if (!enableSearch) return;
    setUserQueryParams({ ...userQueryParams, q: debouncedSearch });

    return () => {
      setUserQueryParams({ ...userQueryParams, q: '' });
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (selectedOptions?.length === options?.length) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [options?.length, selectedOptions?.length]);

  const updateMenuPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  };

  const handleOpenMenu = () => {
    if (!isOpen) {
      updateMenuPosition();
    }
    setIsOpen(!isOpen);
    setIsMoreOptionsOpen(false);
  };

  const handleMoreOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMoreOptionsOpen(!isMoreOptionsOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMoreOptionsOpen(false);
  };

  const handleCloseMoreOptions = () => {
    setIsMoreOptionsOpen(false);
  };

  const selectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAllSelected(true);
    setSelectedOptions(
      optionType,
      options.map((opt) => opt.id?.toString())
    );
    setIsMoreOptionsOpen(false);
  };

  const deselectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAllSelected(false);
    setSelectedOptions(optionType, []);
    setIsMoreOptionsOpen(false);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const value = (e.target as HTMLInputElement & { value: string }).value;

    if (selectedOptions?.includes(value)) {
      setSelectedOptions(
        optionType,
        selectedOptions.filter((opt) => opt !== value)
      );
    } else {
      setSelectedOptions(optionType, [...selectedOptions, value]);
    }
  };

  const singleFilterValue = () => {
    const singleFilter = options?.find(
      (option) => option.id?.toString() === selectedOptions[0]
    );
    return singleFilter?.label;
  };

  const isChecked = (option: { id: number }) => {
    return selectedOptions?.includes(option.id?.toString());
  };

  // Click outside handling
  useClickOutside([menuRef, triggerRef], handleClose, isOpen);
  useClickOutside(
    [moreOptionsMenuRef, menuRef],
    handleCloseMoreOptions,
    isMoreOptionsOpen
  );

  // Handle scroll for infinite loading
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight) {
        onMenuScroll(isActivity);
      }
    }
  };

  // Update position on scroll/resize when menu is open
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
  }, [isOpen]);

  return (
    <>
      <div ref={triggerRef} className="w-full">
        <button
          id={id}
          data-testid={id}
          onClick={handleOpenMenu}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`${header}: ${selectedOptions?.length || 0} selected`}
          className="inline-flex items-center justify-between w-full min-h-8 px-3 py-1.5 rounded-full border border-neutral-200 bg-white text-sm font-semibold"
        >
          <span className="flex items-center gap-2 flex-1 min-w-0">
            {startDecorator}
            {!allSelected && selectedOptions?.length > 1 ? (
              <FilterPill
                selectedOptions={selectedOptions}
                handleClearFilters={deselectAll}
              />
            ) : (
              <span className="flex-1 text-left text-neutral-800 truncate font-normal">
                {!allSelected && selectedOptions?.length === 1
                  ? singleFilterValue()
                  : 'All'}
              </span>
            )}
          </span>
          <Icon
            icon={faCaretDown}
            className={`w-3.5 h-3.5 text-neutral-500 transition-transform duration-200 ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
          />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            data-testid={`${id}-menu`}
            role="listbox"
            aria-label={header}
            className="fixed z-50 w-60 p-2 flex flex-col items-start rounded-lg border border-neutral-300 bg-white shadow-md"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            <div className="flex flex-col w-full">
              {/* Header with more options */}
              <div className="flex justify-between items-center pl-2 pr-1">
                <span className="text-sm font-medium text-neutral-800">
                  {header}
                </span>
                <div className="relative">
                  <button
                    onClick={handleMoreOptionsClick}
                    className="p-1.5 rounded hover:bg-neutral-100 text-neutral-600 transition-colors"
                    aria-label="More Options"
                  >
                    <Icon icon={faEllipsisV} className="h-3 w-3" />
                  </button>
                  {isMoreOptionsOpen && (
                    <div
                      ref={moreOptionsMenuRef}
                      className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-10 min-w-[120px]"
                    >
                      <button
                        data-testid="select-all-btn"
                        onClick={selectAll}
                        className="w-full px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 transition-colors rounded-t-lg"
                      >
                        Select All
                      </button>
                      <button
                        data-testid="deselect-all-btn"
                        onClick={deselectAll}
                        className="w-full px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 transition-colors rounded-b-lg"
                      >
                        Deselect All
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Search input */}
              {enableSearch && (
                <div className="py-2">
                  <Search
                    size="sm"
                    value={search}
                    onSearch={setSearch}
                    placeholder="Search"
                    className="rounded-full"
                  />
                </div>
              )}

              {/* Options list */}
              <div
                ref={containerRef}
                onScroll={handleScroll}
                className="max-h-[200px] w-full overflow-auto"
              >
                {[...options]
                  ?.sort((a, b) => {
                    const aChecked = selectedOptions?.includes(
                      a.id?.toString()
                    );
                    const bChecked = selectedOptions?.includes(
                      b.id?.toString()
                    );
                    return aChecked === bChecked ? 0 : aChecked ? -1 : 1;
                  })
                  .map((option) => {
                    const label = isActivity ? (
                      <ActivityTypeLabel activityId={option.id} />
                    ) : (
                      option.label
                    );
                    return (
                      <div
                        key={option.id}
                        className="flex items-center gap-3 px-2 py-1.5 w-full h-6"
                      >
                        <Checkbox
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          label={label}
                          value={option.id?.toString()}
                          onChange={handleCheckboxChange}
                          checked={isChecked(option)}
                          data-testid={`${id}-checkbox-${option.id}`}
                        />
                      </div>
                    );
                  })}
                {isLoading && (
                  <div className="flex justify-center py-2">
                    <CircularProgress size="sm" />
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MultiSelectMenu;
